import { create } from 'zustand';
import api from '../lib/api';
import type {
  CompetitorFundraiser,
  FundraiserFilters,
  FundraiserStats,
  FundraiserExtractionResponse,
  FundraiserUpdatePayload,
  PaginationMeta,
} from '../types';

// raisedAmount/goalAmount/blastAmount/tipsAmount are Prisma Decimal and arrive
// as strings (or null) over the wire; normalize to number|null at the store
// boundary so every consumer of CompetitorFundraiser sees the typed shape.
const toNumOrNull = (v: unknown): number | null => (v == null || v === '' ? null : Number(v));

function normalizeFundraiser(f: CompetitorFundraiser): CompetitorFundraiser {
  return {
    ...f,
    raisedAmount: toNumOrNull(f.raisedAmount),
    goalAmount: toNumOrNull(f.goalAmount),
    blastAmount: toNumOrNull(f.blastAmount),
    tipsAmount: toNumOrNull(f.tipsAmount),
  };
}

interface FundraiserState {
  // List state
  fundraisers: CompetitorFundraiser[];
  pagination: PaginationMeta | null;
  filters: FundraiserFilters;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;

  // Stats
  stats: FundraiserStats | null;
  isLoadingStats: boolean;

  // Org-specific fundraisers
  orgFundraisers: CompetitorFundraiser[];
  isLoadingOrgFundraisers: boolean;

  // Actions
  fetchFundraisers: () => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchOrgFundraisers: (orgId: string) => Promise<void>;
  setFilters: (filters: FundraiserFilters) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  updateFundraiser: (id: string, data: FundraiserUpdatePayload) => Promise<void>;
  createFundraiser: (data: {
    organizationId: string;
    sportId?: string;
    platform: string;
    year: number;
    campaignUrl?: string | null;
    campaignName?: string | null;
    raisedAmount?: number | null;
    goalAmount?: number | null;
    status?: string;
  }) => Promise<CompetitorFundraiser>;
  extractFromUrl: (url: string) => Promise<FundraiserExtractionResponse>;
}

export const useFundraiserStore = create<FundraiserState>((set, get) => ({
  fundraisers: [],
  pagination: null,
  filters: {},
  currentPage: 1,
  pageSize: 25,
  isLoading: false,
  error: null,
  stats: null,
  isLoadingStats: false,
  orgFundraisers: [],
  isLoadingOrgFundraisers: false,

  fetchFundraisers: async () => {
    set({ isLoading: true, error: null });
    try {
      const { currentPage, pageSize, filters } = get();
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('limit', String(pageSize));
      if (filters.platform) params.set('platform', filters.platform);
      if (filters.state) params.set('state', filters.state);
      if (filters.sport) params.set('sport', filters.sport);
      if (filters.year) params.set('year', String(filters.year));
      if (filters.status) params.set('status', filters.status);
      if (filters.sourcePlatform) params.set('sourcePlatform', filters.sourcePlatform);
      if (filters.isMatched !== undefined) params.set('isMatched', String(filters.isMatched));
      if (filters.search) params.set('search', filters.search);
      if (filters.sortBy) params.set('sortBy', filters.sortBy);
      if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);

      const { data } = await api.get(`/api/fundraisers?${params.toString()}`);
      set({
        fundraisers: (data.fundraisers as CompetitorFundraiser[]).map(normalizeFundraiser),
        pagination: data.pagination,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to fetch fundraisers',
        isLoading: false,
      });
    }
  },

  fetchStats: async () => {
    set({ isLoadingStats: true });
    try {
      const { data } = await api.get('/api/fundraisers/stats');
      set({ stats: data.stats, isLoadingStats: false });
    } catch {
      set({ isLoadingStats: false });
    }
  },

  fetchOrgFundraisers: async (orgId: string) => {
    set({ isLoadingOrgFundraisers: true });
    try {
      const { data } = await api.get(`/api/organizations/${orgId}/fundraisers`);
      set({ orgFundraisers: (data.fundraisers as CompetitorFundraiser[]).map(normalizeFundraiser), isLoadingOrgFundraisers: false });
    } catch {
      set({ orgFundraisers: [], isLoadingOrgFundraisers: false });
    }
  },

  setFilters: (filters: FundraiserFilters) => {
    set({ filters, currentPage: 1 });
    get().fetchFundraisers();
  },

  setPage: (page: number) => {
    set({ currentPage: page });
    get().fetchFundraisers();
  },

  setPageSize: (size: number) => {
    set({ pageSize: size, currentPage: 1 });
    get().fetchFundraisers();
  },

  updateFundraiser: async (id, data) => {
    await api.patch(`/api/fundraisers/${id}`, data);
  },

  createFundraiser: async (data) => {
    const res = await api.post<{ fundraiser: CompetitorFundraiser }>('/api/fundraisers', data);
    return normalizeFundraiser(res.data.fundraiser);
  },

  extractFromUrl: async (url: string) => {
    const { data } = await api.post<FundraiserExtractionResponse>('/api/fundraisers/extract', { url });
    return { extraction: data.extraction, extractionStatus: data.extractionStatus, extractionReason: data.extractionReason };
  },
}));
